import type { I18nLike } from '@/types/changeRequest';

interface GenericPayloadProps {
  /** Any payload; a non-object renders the empty notice. */
  payload: unknown;
  i18n: I18nLike;
  'data-testid'?: string;
}

/**
 * GenericPayload is the key / value fallback for a workflow payload without a
 * dedicated renderer, or one whose shape failed its type guard. Lives in its
 * own module so both `WorkflowPayloadDetail` and the per-family renderers can
 * use it without an import cycle.
 */
export function GenericPayload({ payload, i18n, 'data-testid': testId }: GenericPayloadProps) {
  const entries =
    payload && typeof payload === 'object'
      ? Object.entries(payload as Record<string, unknown>).filter(
          ([, value]) => value !== null && value !== undefined && value !== '',
        )
      : [];

  if (entries.length === 0) {
    return (
      <p data-testid={testId} className="text-sm text-muted-foreground">
        {i18n._({ id: 'workflow.payload.empty', message: 'No additional details' })}
      </p>
    );
  }

  return (
    <div data-testid={testId} className="space-y-3">
      {entries.map(([key, value]) => (
        <div key={key}>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}
          </span>
          <div className="mt-1">
            <p className="break-words text-sm">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
